import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApplicationConstants } from '@constants/app.constants';
import {
  IconoirXmark,
  IconoirUpload,
  IconoirDownload,
} from '@ey-xd/motif-icon';
import {
  MotifModalModule,
  MotifChipModule,
  MotifButtonModule,
  MotifIconModule,
  MotifFormsModule,
  ButtonSizes,
  MOTIF_MODAL_DATA,
  MotifModal,
} from '@ey-xd/ng-motif';
import { TranslatePipe, TranslateDirective } from '@ngx-translate/core';
import { CommonService } from '@services/common.service';
import { SecureStorageService } from '@services/securestorage.service';
import { UploadService } from '@services/upload.service';

@Component({
  selector: 'app-masterdata-upload',
  imports: [
    CommonModule,
    MotifModalModule,
    TranslatePipe,
    TranslateDirective,
    MotifChipModule,
    MotifButtonModule,
    MotifIconModule,
    MotifFormsModule,
    FormsModule,
  ],
  templateUrl: './masterdata-upload.component.html',
  styleUrl: './masterdata-upload.component.scss',
})
export class MasterdataUploadComponent {
  buttonSizes = ButtonSizes;
  closeIcon = IconoirXmark;
  uploadIcon = IconoirUpload;
  downloadIcon = IconoirDownload;
  sessionService = inject(SecureStorageService);
  readonly data = inject<any>(MOTIF_MODAL_DATA);
  fileLoaded: boolean = false;

  fileName: string = '';
  fileSize = '';
  errorMessage: string = '';
  files: { name: string; size: string; file: File }[] = [];
  maxFileSize = 25;
  allowedExtensions = ['xls', 'xlsx'];
  fullReplacement: boolean = false;
  module: string;
  isIPORModule = false;

  constructor(
    private readonly commonService: CommonService,
    private readonly modal: MotifModal,
    private readonly uploadService: UploadService,
  ) {}

  ngOnInit() {
    this.module =
      this.sessionService.getSessionStorageData('activeModule') ||
      ApplicationConstants.PMPRO;
    this.isIPORModule = this.module === ApplicationConstants.IPOR;
  }

  formatFileSize(size: number): string {
    if (size < 1024) {
      // size is in bytes
      return size + 'B';
    } else if (size < 1024 * 1024) {
      // size is in kilobytes
      return (size / 1024).toFixed(2) + 'KB';
    } else {
      // size is in megabytes
      return (size / 1024 / 1024).toFixed(2) + 'MB';
    }
  }

  handleFiles(files: File[]) {
    const maxFileSize = this.maxFileSize * 1024 * 1024; // 25 MB in bytes

    this.files = files
      .filter((file) => {
        const extension = file.name.split('.').pop()?.toLowerCase();
        const isAllowedExtension = this.allowedExtensions.includes(
          extension || '',
        );
        const isWithinSizeLimit = file.size <= maxFileSize;

        if (!isAllowedExtension) {
          this.commonService.showTranslatedToasterMessage(
            'FormatError',
            { allowedExtensions: this.allowedExtensions.join(', ') },
            'error',
          );
        } else if (!isWithinSizeLimit) {
          this.commonService.showTranslatedToasterMessage(
            'SizeError',
            { limit: this.maxFileSize },
            'error',
          );
        }

        return isAllowedExtension && isWithinSizeLimit;
      })
      .map((file) => {
        return {
          name: file.name,
          size: this.formatFileSize(file.size),
          file: file,
        };
      });

    if (this.files.length === 1) {
      this.fileName = this.files[0].name;
      this.fileSize = this.files[0].size;
    }
    this.fileLoaded = this.files.length > 0;
  }

  downloadTemplate() {
    this.uploadService.downloadMasterDataTemplate().subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'QuestionBankTemplate.xlsx'; // Change the file name as needed
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        this.commonService.showTranslatedToasterMessage(
            'DownloadTemplateError',
            null,
            'error',
          );
        console.error('Error downloading question template file:', error);
      },
    });
  }

  onValueChange($event: File | FileList) {
    if ($event instanceof File) {
      this.handleFiles([$event]);
    } else {
      this.handleFiles(Array.from($event));
    }
  }

  onDrop($event: File | FileList) {
    if ($event instanceof File) {
      this.handleFiles([$event]);
    } else {
      this.handleFiles(Array.from($event));
    }
  }

  proceed() {
    const file = this.files[0].file;
    if (file) {
      // this.commonService.startLoader();
      this.uploadService
        .uploadTaskMasterData(
          this.files[0].file,
          this.fullReplacement,
          this.module,
        )
        .subscribe({
          next: () => {
            this.commonService.showTranslatedToasterMessage(
              'UploadSuccess',
              null,
              'success',
            );
            this.commonService.notifyUploadSuccess();
            // this.commonService.stopLoader();
          },
          error: (err) => {
            console.error(err);
            this.commonService.showTranslatedToasterMessage(
              'UploadFailed',
              null,
              'error',
            );
            // this.commonService.stopLoader();
          },
          complete: () => {
            this.files = [];
            this.fileName = '';
            this.fileSize = '';
            this.fileLoaded = false;
            // this.commonService.stopLoader();
            this.modal.closeAll();
          },
        });
    }
  }

  removeFile(evt) {
    console.log('Remove file', evt);
    this.files = [];
    this.fileName = '';
    this.fileSize = '';
    this.fileLoaded = false;
  }

  onUploaderKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.removeFile(event);
    }
  }
}
